// The engine contract. Frozen after Wave 0; extended additively in Wave 1 (Scotch + proxy)
// and since only by optional fields (BBBConfig.autoBongo, 2026-09-30).
// Nothing that existed before changed shape — only new members were added to GameType and
// InputKind, a new ScotchConfig, and optional fields on WolfConfig.
// Engines are pure TypeScript: they may import ONLY from this file, ../types,
// ./handicap and ./scoreUtils. Never React/react-native/store.

import type { Course, Round, RoundPlayer } from '../types';

export type GameType =
  | 'stroke'
  | 'stableford'
  | 'matchplay'
  | 'bestball'
  | 'skins'
  | 'nassau'
  | 'wolf'
  | 'vegas'
  | 'highlow'
  | 'niner'
  | 'banker'
  | 'bbb'
  | 'snake'
  | 'sixes'
  | 'rabbit'
  | 'dots'
  | 'leftright'
  | 'scotch'
  | 'scramble'
  | 'longdrive';

/** All money values everywhere in the engine layer are INTEGER CENTS. */
export type Cents = number;

export type BaseConfig = {
  scoring: 'gross' | 'net';
  /** usga-diff: low course handicap plays scratch, others receive the difference. full: everyone receives full CH. */
  handicapMode: 'usga-diff' | 'full';
  participantIds: string[];
  /**
   * Friendly / gentleman's match: set to 'points' by friendlyConfig.rewriteConfigFriendly on
   * the COPY of config handed to compute() — never on the config persisted on the
   * GameInstance. Read only by the handful of engines that bake a formatted amount directly
   * into a StandingRow.primary or HoleResultRow.perPlayer/summary string (the engine can't
   * see GameInstance.friendly, only this). Absent or 'money' means real dollars, same as
   * always — every round persisted before this field existed has neither.
   */
  units?: 'money' | 'points';
};

export type StrokeConfig = BaseConfig & { type: 'stroke'; potPerPlayer: Cents };
export type StablefordConfig = BaseConfig & {
  type: 'stableford';
  table: 'classic' | 'grint';
  perPoint: Cents; // pairwise: |ptsA - ptsB| × perPoint between every pair
};
export type MatchplayConfig = BaseConfig & { type: 'matchplay'; perMatch: Cents }; // exactly 2 participants
export type BestballConfig = BaseConfig & {
  type: 'bestball';
  teams: [string[], string[]];
  perPoint: Cents; // per hole won
};
export type SkinsConfig = BaseConfig & {
  type: 'skins';
  perSkin: Cents;
  carryover: boolean;
  /** winner must tie-or-beat the field on the NEXT hole to keep the skin */
  validation: boolean;
};
export type NassauConfig = BaseConfig & {
  type: 'nassau';
  perBet: Cents; // value of each of front/back/overall
  presses: 'off' | 'auto2down' | 'manual';
  /** null = singles (exactly 2 participants); otherwise 2v2 best ball */
  teams: [string[], string[]] | null;
};
export type WolfConfig = BaseConfig & {
  type: 'wolf';
  perPoint: Cents;
  loneMult: number; // default 2
  blindMult: number; // default 3
  /** Optional closest-to-the-pin side bet, settled on top of the hole's wolf result.
   *  All three are optional so rounds persisted before Wave 1 keep computing. */
  proxy?: boolean;
  proxyValue?: Cents; // default 100
  proxyPar3Only?: boolean; // default false — proxy is CTP in regulation on any hole
};
export type VegasConfig = BaseConfig & {
  type: 'vegas';
  teams: [string[], string[]];
  perPoint: Cents;
  birdieFlip: boolean;
};
export type HighLowConfig = BaseConfig & {
  type: 'highlow';
  teams: [string[], string[]];
  perPoint: Cents;
};
export type NinerConfig = BaseConfig & { type: 'niner'; perPoint: Cents }; // exactly 3 participants
export type BankerConfig = BaseConfig & {
  type: 'banker';
  minWager: Cents;
  maxWager: Cents;
};
export type BBBConfig = BaseConfig & {
  type: 'bbb';
  perPoint: Cents;
  /** Bongo goes to the hole's outright low score (net when scoring is net) wherever nobody
   *  tapped one. Optional: rounds saved before this read absent as off, so a settled round's
   *  money never moves. New games default it on. */
  autoBongo?: boolean;
};
export type SnakeConfig = BaseConfig & {
  type: 'snake';
  perPlayer: Cents; // holder at round end pays this to each other participant
  doubles: boolean; // pot doubles each time the snake changes hands
  /** Ceiling on the doubling multiplier. Optional: rounds saved before this default to 8. */
  maxDouble?: number;
};
export type SixesConfig = BaseConfig & { type: 'sixes'; perSegment: Cents }; // exactly 4 participants
export type RabbitConfig = BaseConfig & { type: 'rabbit'; perLeg: Cents }; // legs settle at holes 6/12/18 (or n/3 marks)
export type DotsConfig = BaseConfig & {
  type: 'dots';
  perDot: Cents; // pairwise dot-differential × perDot
  junkValue: number; // dots per junk flag (default 1)
  /** A greenie is a par-3 junk. Optional: rounds saved before this default to true. */
  greeniePar3Only?: boolean;
};
export type LeftRightConfig = BaseConfig & {
  type: 'leftright';
  variant: 'center' | 'zones';
  perPoint: Cents;
};
/** Scotch (a.k.a. Bridge): 6 points a hole — 2 low ball, 2 low total, 1 proxy, 1 birdie. */
export type ScotchConfig = BaseConfig & {
  type: 'scotch';
  perPoint: Cents;
  /** rotate6: partners change every 6 holes (seat pairs, same rotation as Sixes). */
  partners: 'rotate6' | 'fixed';
  /** Only read when partners === 'fixed'. */
  teams: [string[], string[]] | null;
  /** The sixth point: a birdie on the hole, or the lower team putt total. */
  fourthPoint: 'birdie' | 'lowPutts';
  /** One team taking all 6 points doubles the hole. */
  sweepBonus: boolean;
};
/**
 * Scramble: everyone on a team hits, the best shot is chosen, and they all play from there.
 * One score per team per hole — the format nearly every charity outing is run under.
 *
 * Unlike the 2-team games this takes N teams, because a round may carry a single team (the
 * usual outing case: one foursome, one card, the leaderboard assembled across rounds at the
 * tournament level) or two for a match inside the group.
 */
export type ScrambleConfig = BaseConfig & {
  type: 'scramble';
  teams: string[][];
  /**
   * Percentage of each member's course handicap that counts toward the team's, applied to
   * the team sorted LOW handicap first. Absent uses the common allocation for the team size.
   * Only read when scoring is 'net'.
   */
  handicapPercents?: number[];
  /** Optional side pot per player, paid by the losing team(s). Zero for a pure outing. */
  perTeam?: Cents;
};

/**
 * Longest Drive: the tee-box sign, run as a bet inside the group and settled on measured
 * yards instead of an argument.
 *
 * The only game in the app that reads no score at all. That is what makes it the one you can
 * add on the seventh tee — it does not care what happened on the first six, and it never
 * needs a hole to be "complete". Holes nobody measured are simply not part of it.
 */
export type LongDriveConfig = BaseConfig & {
  type: 'longdrive';
  /** What one hole's longest drive is worth, paid by each other player who marked a drive. */
  perHole: Cents;
  /** Restricts the win to drives marked as finishing in the fairway. */
  fairwayOnly: boolean;
};

export type GameConfig =
  | StrokeConfig
  | StablefordConfig
  | MatchplayConfig
  | BestballConfig
  | SkinsConfig
  | NassauConfig
  | WolfConfig
  | VegasConfig
  | HighLowConfig
  | NinerConfig
  | BankerConfig
  | BBBConfig
  | SnakeConfig
  | SixesConfig
  | RabbitConfig
  | DotsConfig
  | LeftRightConfig
  | ScotchConfig
  | ScrambleConfig
  | LongDriveConfig;

export type GameInstance = {
  id: string;
  config: GameConfig;
  /** Set when this game came from a Tournament game template — the key money aggregates on.
   *  Survives per-round edits, so "day 1 Wolf, day 2 Nassau" still tallies to the right line. */
  templateId?: string;
  /**
   * Friendly / gentleman's match: compute() runs on a copy of `config` with every money
   * field rewritten to 1 point (see engine/friendlyConfig.ts) instead of the real stakes —
   * `config` itself is never touched, so switching this back off is lossless. Absent (the
   * only shape a round persisted before this read) means real money, same as `false`.
   */
  friendly?: boolean;
};

/** Extra per-hole inputs a game needs the HoleEntryScreen to surface. */
export type InputKind =
  | 'putts'
  | 'junk'
  | 'bbb'
  | 'wolfPick'
  | 'bankerWager'
  | 'leftRightZone'
  | 'nassauPress'
  | 'proxy'
  | 'snakeTie'
  | 'drive';

export type StandingRow = {
  playerId?: string; // one of the two is set
  teamLabel?: string;
  primary: string; // "42 pts", "3 & 2", "+4"
  detail?: string; // "2 skins", "holds the snake"
  money: Cents; // running net for this game (Σ over rows = 0)
};

export type HoleResultRow = {
  hole: number; // 1-based
  summary: string; // "Skin $6 → Pat (carry ×2)"
  perPlayer?: Record<string, string>; // playerId → short cell, e.g. "5 (n4)"
};

export type LedgerEntry = { playerId: string; amount: Cents }; // Σ per game ≈ 0

export type GameResult = {
  /** Engines return '' — the caller (store selector) stamps the GameInstance id. */
  gameId: string;
  type: GameType;
  standings: StandingRow[];
  holeRows: HoleResultRow[];
  ledger: LedgerEntry[];
  throughHole: number; // last hole fully counted (1-based, 0 = none)
  notes?: string[]; // "Hole 14 skipped: no Wolf pick recorded"
};

/**
 * Hide a config field until another field holds a given value. Presentational only —
 * the key is always present on the config object, and engines never read this.
 * Keeps Scotch's team picker off screen while partners are rotating, and Wolf's proxy
 * amount off screen while the proxy bet is switched off.
 */
export type FieldVisibility = { key: string; equals: string | number | boolean };

/** Drives the generic GameConfigScreen. */
export type FieldDescriptor = { showIf?: FieldVisibility } & (
  | { key: string; label: string; type: 'money'; default: Cents; min?: Cents; step?: Cents }
  | { key: string; label: string; type: 'number'; default: number; min?: number; max?: number }
  | { key: string; label: string; type: 'toggle'; default: boolean }
  | {
      key: string;
      label: string;
      type: 'select';
      options: { value: string; label: string }[];
      default: string;
    }
  | { key: string; label: string; type: 'teams' }
);

export type RoundCtx = {
  course: Course;
  round: Round;
  /** Per participant, per hole: handicap strokes received under THIS game's config (see handicap.buildCtx). */
  strokesReceived: Record<string, number[]>;
};

export interface GameEngine<C extends GameConfig = GameConfig> {
  type: GameType;
  meta: {
    name: string;
    blurb: string; // one-liner for the game catalog
    /** Other names players search by (e.g. "Bridge" for Scotch). Optional — most games have none. */
    aliases?: string[];
    minPlayers: number;
    maxPlayers: number;
    inputs: InputKind[];
    needsTeams?: boolean;
  };
  configFields: FieldDescriptor[];
  defaultConfig(participants: RoundPlayer[]): C;
  /** Config-time problems, e.g. "Wolf needs exactly 4 players". Empty = valid. */
  validate(ctx: RoundCtx, config: C): string[];
  /** PURE. Must tolerate partial rounds (null scores) — count only holes complete for this game. */
  compute(ctx: RoundCtx, config: C): GameResult;
}

export const emptyResult = (gameId: string, type: GameType): GameResult => ({
  gameId,
  type,
  standings: [],
  holeRows: [],
  ledger: [],
  throughHole: 0,
});
